import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API User response type
interface ApiUser {
  userId: number;
  userName: string;
  description: string | null;
  isAdmin: boolean;
  isActive: boolean;
  clientId: number | null;
  mfaEnabled: boolean;
  blocked: boolean;
  entity: string;
  roleIds?: number[];
  roleNames?: string[];
}

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roleId: number;
  roleName: string;
  isActive: boolean;
  isAdmin: boolean;
  clientId?: number;
  clientName?: string;
  mfaEnabled: boolean;
  blocked: boolean;
  entity: string;
  lastLoginDate?: string;
  roleIds?: number[];
  roleNames?: string[];
}

export interface UserSearchParams {
  search?: string;
  roleId?: number;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
  clientId?: number;
  backofficeOnly?: boolean;
}

export interface CreateUserRequest {
  username: string;
  password: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roleId?: number;
  description?: string;
  isAdmin?: boolean;
  clientId?: number;
  isActive?: boolean;
  roleIds?: number[];
  mfaEnabled?: boolean;
  blocked?: boolean;
}

export interface UpdateUserRequest {
  id: number;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roleId?: number;
  description?: string;
  isAdmin?: boolean;
  clientId?: number;
  isActive?: boolean;
  password?: string;
  roleIds?: number[];
  // Optional: omit to preserve current value on the server. Pass explicit
  // true/false to set. See UserSaveRequest in UserController.cs.
  mfaEnabled?: boolean;
  blocked?: boolean;
}

export interface ChangePasswordRequest {
  userId: number;
  currentPassword: string;
  newPassword: string;
}

export interface ResetPasswordRequest {
  userId: number;
  newPassword: string;
  confirmPassword: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  permissions: string[];
}

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UserSearchParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: number) => [...userKeys.details(), id] as const,
  roles: () => [...userKeys.all, 'roles'] as const,
};

// Transform API User to our User interface
function transformUser(apiUser: ApiUser): User {
  const roleNames = apiUser.roleNames ?? [];
  return {
    id: apiUser.userId,
    username: apiUser.userName,
    email: '', // API doesn't return email
    firstName: '',
    lastName: '',
    fullName: apiUser.description || apiUser.userName,
    roleId: apiUser.isAdmin ? 1 : 2,
    roleName: roleNames.length > 0 ? roleNames.join(', ') : (apiUser.isAdmin ? 'Administrator' : 'User'),
    isActive: apiUser.isActive,
    isAdmin: apiUser.isAdmin,
    clientId: apiUser.clientId || undefined,
    mfaEnabled: apiUser.mfaEnabled,
    blocked: apiUser.blocked,
    entity: apiUser.entity,
    roleIds: apiUser.roleIds ?? [],
    roleNames: roleNames,
  };
}

// Get all users
export function useUsers(params: UserSearchParams = {}) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: async () => {
      const queryParams: Record<string, string | number | boolean> = {};
      if (params.clientId !== undefined) queryParams.clientId = params.clientId;
      if (params.backofficeOnly) queryParams.backofficeOnly = true;

      const response = await api.get<ApiResponse<ApiUser[]>>('/api/user/list', {
        params: queryParams,
      });
      let users = response.data.data.map(transformUser);

      // Client-side filtering
      if (params.search) {
        const search = params.search.toLowerCase();
        users = users.filter(u =>
          u.username.toLowerCase().includes(search) ||
          u.fullName.toLowerCase().includes(search)
        );
      }
      if (params.isActive !== undefined) {
        users = users.filter(u => u.isActive === params.isActive);
      }
      if (params.roleId) {
        users = users.filter(u => u.roleIds?.includes(params.roleId!));
      }

      // Apply pagination
      const page = params.page || 1;
      const pageSize = params.pageSize || 25;
      const startIndex = (page - 1) * pageSize;
      const paginatedItems = users.slice(startIndex, startIndex + pageSize);

      return {
        items: paginatedItems,
        totalCount: users.length,
        totalPages: Math.ceil(users.length / pageSize),
        page,
        pageSize,
      };
    },
  });
}

// Get single user
export function useUser(id: number, enabled = true) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiUser>>('/api/user/get', {
        params: { userId: id },
      });
      return transformUser(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Create user
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/user/save', {
        userId: 0,
        userName: data.username,
        password: data.password,
        description: data.description,
        isAdmin: data.isAdmin ?? false,
        isActive: data.isActive ?? true,
        clientId: data.clientId,
        roleIds: data.roleIds,
        mfaEnabled: data.mfaEnabled,
        blocked: data.blocked,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

// Update user
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateUserRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/user/save', {
        userId: data.id,
        // Backend's UserSaveRequest.UserName is a non-nullable reference type,
        // so ASP.NET rejects the request with a 400 validation error when it's
        // missing. Always echo the current username back, even on edit.
        userName: data.username,
        description: data.description,
        isAdmin: data.isAdmin,
        isActive: data.isActive,
        clientId: data.clientId,
        password: data.password,
        roleIds: data.roleIds,
        mfaEnabled: data.mfaEnabled,
        blocked: data.blocked,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
    },
  });
}

// Delete user (soft delete)
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.post<ApiResponse<unknown>>('/api/user/delete', { userId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

// Toggle user active status
export function useToggleUserActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      // Get current user data first
      const userResponse = await api.get<ApiResponse<ApiUser>>('/api/user/get', {
        params: { userId: id },
      });
      const user = userResponse.data.data;

      // Update with new active status
      const response = await api.post<ApiResponse<number>>('/api/user/save', {
        userId: id,
        userName: user.userName,
        description: user.description,
        isAdmin: user.isAdmin,
        isActive: isActive,
        clientId: user.clientId,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) });
    },
  });
}

// Change user password
export function useChangeUserPassword() {
  return useMutation({
    mutationFn: async (data: ChangePasswordRequest) => {
      await api.post<ApiResponse<unknown>>('/api/user/changePassword', {
        userId: data.userId,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    },
  });
}

// Admin-only: reset a user's password without requiring the current password
export function useResetUserPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ResetPasswordRequest) => {
      await api.post<ApiResponse<unknown>>('/api/user/resetPassword', {
        userId: data.userId,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Get roles from the API for use as a checkbox/selector list on the User form.
 *
 * `clientId` scopes the request to a specific client:
 *  - `undefined` — legacy: backend picks backoffice roles (admin JWT) or the
 *    caller's own client-scoped roles (client-portal JWT).
 *  - `number`    — admin is asking for the roles owned by that client
 *    (Clients Hub).
 *  - `null`      — explicitly request backoffice (ClientId IS NULL) roles.
 */
export interface UseUserRolesOptions {
  enabled?: boolean;
  clientId?: number | null;
}

export function useUserRoles(options?: UseUserRolesOptions) {
  const clientId = options?.clientId;
  return useQuery({
    queryKey: [...userKeys.roles(), { clientId: clientId ?? null }],
    queryFn: async () => {
      const params: Record<string, number> = {};
      if (typeof clientId === 'number') {
        params.clientId = clientId;
      }
      const response = await api.get<ApiResponse<{ roleId: number; roleName: string; description?: string }[]>>(
        '/api/role/list',
        { params }
      );
      return response.data.data.map((r) => ({
        id: r.roleId,
        name: r.roleName,
        description: r.description ?? '',
        permissions: [],
      })) as Role[];
    },
    enabled: options?.enabled ?? true,
  });
}

// Get users for dropdown (lightweight list for selectors)
export function useUsersDropdown() {
  return useQuery({
    queryKey: [...userKeys.all, 'dropdown'],
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiUser[]; totalRecords: number }>>('/api/user/search', {
        page: 1,
        pageSize: 200,
      });
      return response.data.data.data.map((user) => ({
        userId: user.userId,
        userName: user.userName,
      }));
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
